import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, Gauge, GitBranch, Bug, FileWarning, Play, Loader2, Check, X, RotateCcw, AlertTriangle } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

function List({ title, items, tone }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</div>
      <ul className="mt-1 space-y-1 text-[13px] text-slate-700">
        {items.map((x, i) => <li key={i} className="flex gap-2"><span className={tone === "for" ? "text-emerald-500" : tone === "against" ? "text-red-500" : "text-slate-300"}>•</span>{x}</li>)}
      </ul>
    </div>
  );
}

export default function AISafety() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("confidence");
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState("");
  const [assessment, setAssessment] = useState(null);
  const [dlpText, setDlpText] = useState("");
  const [dlpDest, setDlpDest] = useState("external");
  const [dlpResult, setDlpResult] = useState(null);
  const [verDraft, setVerDraft] = useState({});
  const load = useCallback(() => { api.get("/ai-safety/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { agents, dlp_patterns, dlp_recent, redteam_last, authority_granted, decisions } = data;

  const assess = async () => {
    if (!question.trim()) return toast.error("Describe the decision.");
    setBusy(true); setAssessment(null);
    try { const { data } = await api.post("/ai-safety/decisions/assess", { question }); setAssessment(data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Assessment failed."); } finally { setBusy(false); }
  };
  const runRedteam = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai-safety/redteam/run"); toast.success(`Red-team: ${data.passed}/${data.total} passed`); load(); }
    catch { toast.error("Red-team failed."); } finally { setBusy(false); }
  };
  const scanDlp = async () => {
    setBusy(true); setDlpResult(null);
    try { const { data } = await api.post("/ai-safety/dlp/scan", { text: dlpText, destination: dlpDest }); setDlpResult(data); load(); }
    catch { toast.error("Scan failed."); } finally { setBusy(false); }
  };
  const saveVersion = async (agent) => {
    const d = verDraft[agent];
    if (!d?.prompt?.trim()) return toast.error("Enter the new prompt.");
    try { await api.post(`/ai-safety/versions/${encodeURIComponent(agent)}`, d); toast.success("New version published"); setVerDraft({ ...verDraft, [agent]: null }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };
  const rollback = async (agent, version) => { try { await api.post(`/ai-safety/versions/${encodeURIComponent(agent)}/rollback`, { version }); toast.success(`Rolled back to v${version}`); load(); } catch { toast.error("Could not roll back."); } };

  const TABS = [["confidence", "Decision Confidence", Gauge], ["versions", "Version Control", GitBranch], ["redteam", "Red-Team", Bug], ["dlp", "DLP", FileWarning]];
  const DEC_CLS = { ALLOW: "bg-emerald-50 text-emerald-600", ESCALATE: "bg-amber-50 text-amber-700", BLOCK: "bg-red-50 text-red-600" };

  return (
    <div className="animate-fade-up space-y-6" data-testid="ai-safety-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><ShieldAlert size={22} className="text-[#b0862f]" /> AI Safety</h1>
          <p className="mt-1 text-[13px] text-slate-500">Confidence on every decision, versioned agent instructions, adversarial red-teaming, and data-loss prevention.</p>
        </div>
        <div className={`rounded-xl px-4 py-2 text-[13px] font-bold ${authority_granted ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`} data-testid="authority-badge">
          {authority_granted ? "✓ Autonomy authority granted" : "✕ Authority withheld — run red-team"}
        </div>
      </div>

      <div className="flex flex-wrap gap-5 border-b border-slate-200">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`safety-tab-${id}`}
            className={`flex items-center gap-1.5 pb-2.5 text-[14px] font-semibold transition-colors ${tab === id ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "confidence" && (
        <div className="space-y-4" data-testid="decision-confidence">
          <Card className="p-5">
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Assess a decision</div>
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} data-testid="decision-question"
              className="w-full rounded-lg border border-slate-200 p-3 text-[14px] outline-none focus:border-[#1a5fd0]" placeholder="e.g. Should we raise the Build plan from $54.99 to $64.99?" />
            <button onClick={assess} disabled={busy} data-testid="assess-btn" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Gauge size={15} />} Assess with AI</button>
          </Card>
          {assessment && (
            <Card className="space-y-3 p-5" data-testid="assessment-result">
              <div className="text-[15px] font-bold text-slate-900">{assessment.recommendation}</div>
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-400"><span>Confidence</span><span>{assessment.confidence}%</span></div>
                <div className="mt-1 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${assessment.confidence >= 66 ? "bg-emerald-500" : assessment.confidence >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${assessment.confidence}%` }} /></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <List title="Evidence for" items={assessment.evidence_for} tone="for" />
                <List title="Evidence against" items={assessment.evidence_against} tone="against" />
                <List title="Assumptions" items={assessment.assumptions} />
                <List title="Missing information" items={assessment.missing_info} />
              </div>
              <List title="What would change this" items={assessment.what_would_change} />
            </Card>
          )}
          {decisions.length > 0 && <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Recent: {decisions.map((d) => `${d.question.slice(0, 40)}… (${d.confidence}%)`).join("  ·  ")}</div>}
        </div>
      )}

      {tab === "versions" && (
        <div className="space-y-3" data-testid="version-control">
          {agents.map((a) => (
            <Card key={a.agent} className="p-4" data-testid="agent-versions">
              <div className="flex items-center justify-between">
                <div className="font-head text-[15px] font-bold text-slate-900">{a.agent} <span className="ml-1 font-mono text-[11px] text-[#1a5fd0]">v{a.active_version} active</span></div>
                <button onClick={() => setVerDraft({ ...verDraft, [a.agent]: verDraft[a.agent] ? null : { prompt: a.versions.find((v) => v.active)?.prompt || "", note: "" } })} className="text-[12px] font-semibold text-[#1a5fd0] hover:underline">New version</button>
              </div>
              {verDraft[a.agent] && (
                <div className="mt-2 rounded-lg border border-slate-200 p-3">
                  <textarea value={verDraft[a.agent].prompt} onChange={(e) => setVerDraft({ ...verDraft, [a.agent]: { ...verDraft[a.agent], prompt: e.target.value } })} rows={3} data-testid="version-prompt" className="w-full rounded-lg border border-slate-200 p-2 font-mono text-[12px] outline-none focus:border-[#1a5fd0]" />
                  <input value={verDraft[a.agent].note} onChange={(e) => setVerDraft({ ...verDraft, [a.agent]: { ...verDraft[a.agent], note: e.target.value } })} placeholder="What changed?" className="mt-2 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[12px] outline-none focus:border-[#1a5fd0]" />
                  <button onClick={() => saveVersion(a.agent)} data-testid="save-version-btn" className="mt-2 rounded-lg bg-[#080d1a] px-4 py-1.5 text-[12px] font-bold text-white">Publish version</button>
                </div>
              )}
              <div className="mt-2 space-y-1">
                {a.versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between border-t border-slate-50 py-1.5 text-[12px]">
                    <span className="text-slate-600"><b>v{v.version}</b> · {v.note} <span className="text-slate-400">· {v.author}</span> {v.active && <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 font-mono text-[9px] font-bold text-emerald-700">ACTIVE</span>}</span>
                    {!v.active && <button onClick={() => rollback(a.agent, v.version)} data-testid="rollback-btn" className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#1a5fd0]"><RotateCcw size={12} /> Roll back</button>}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "redteam" && (
        <div className="space-y-3" data-testid="redteam">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-slate-500">Adversarial tests run against your live Policy Engine. Every critical test must pass for autonomy authority.</p>
            <button onClick={runRedteam} disabled={busy} data-testid="run-redteam-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2.5 font-head text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run red-team</button>
          </div>
          {!redteam_last ? <Card className="p-8 text-center text-[13px] text-slate-400">No red-team run yet. Run it to validate your safety controls.</Card> : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat label="Tests passed" value={`${redteam_last.passed}/${redteam_last.total}`} tone={redteam_last.pass_rate === 100 ? "green" : "red"} />
                <Stat label="Pass rate" value={`${redteam_last.pass_rate}%`} tone="blue" />
                <Stat label="Authority" value={redteam_last.authority_granted ? "Granted" : "Withheld"} tone={redteam_last.authority_granted ? "green" : "red"} />
              </div>
              <Card className="p-4">
                {redteam_last.results.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border-b border-slate-50 py-2 text-[13px] last:border-0" data-testid="redteam-result">
                    <span className="flex items-center gap-2">{r.passed ? <Check size={15} className="text-emerald-500" /> : <X size={15} className="text-red-500" />}<span className="text-slate-700">{r.name}</span></span>
                    <span className="font-mono text-[10px] text-slate-400">expected {r.expected} · got {r.got}</span>
                  </div>
                ))}
              </Card>
            </>
          )}
        </div>
      )}

      {tab === "dlp" && (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="dlp">
          <Card className="p-5">
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Scan outbound content</div>
            <textarea value={dlpText} onChange={(e) => setDlpText(e.target.value)} rows={5} data-testid="dlp-text" className="w-full rounded-lg border border-slate-200 p-3 text-[13px] outline-none focus:border-[#1a5fd0]" placeholder="Paste an email, export or AI prompt to check for sensitive data (try an SSN like 123-45-6789)…" />
            <div className="mt-3 flex items-center gap-3">
              <select value={dlpDest} onChange={(e) => setDlpDest(e.target.value)} data-testid="dlp-destination" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]">
                <option value="external">External destination</option>
                <option value="internal">Internal destination</option>
              </select>
              <button onClick={scanDlp} disabled={busy} data-testid="dlp-scan-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-5 py-2 text-[13px] font-bold text-white disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <FileWarning size={14} />} Scan</button>
            </div>
            {dlpResult && (
              <div className="mt-4" data-testid="dlp-result">
                <span className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 font-head text-lg font-extrabold ${DEC_CLS[dlpResult.decision]}`}>{dlpResult.decision === "ALLOW" ? <Check size={20} /> : dlpResult.decision === "BLOCK" ? <X size={20} /> : <AlertTriangle size={20} />} {dlpResult.decision}</span>
                {dlpResult.findings.length > 0 && <ul className="mt-2 space-y-1 text-[13px] text-slate-700">{dlpResult.findings.map((f, i) => <li key={i}>• {f.name} <span className="font-mono text-[10px] uppercase text-slate-400">{f.severity}</span></li>)}</ul>}
              </div>
            )}
          </Card>
          <Card className="p-5">
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Detection patterns</div>
            <div className="space-y-1.5">{dlp_patterns.map((p) => <div key={p.id} className="flex items-center justify-between text-[13px]"><span className="text-slate-700">{p.name}</span><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{p.severity}</span></div>)}</div>
            {dlp_recent.length > 0 && <><div className="mt-4 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Recent scans</div><div className="mt-1 space-y-1">{dlp_recent.map((s) => <div key={s.id} className="flex items-center justify-between text-[12px]"><span className={`font-mono ${s.decision === "BLOCK" ? "text-red-600" : s.decision === "ESCALATE" ? "text-amber-600" : "text-emerald-600"}`}>{s.decision}</span><span className="text-slate-400">{s.destination} · {s.findings.length} finding(s)</span></div>)}</div></>}
          </Card>
        </div>
      )}
    </div>
  );
}
